import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Cloud as CloudIcon, AutoAwesome as SparkIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { SolutionStory, solutionStoriesApi } from '../../services/api';
import {
  buildTagline,
  clientShortName,
  collectPoints,
  splitCsv,
  StoryTab,
} from './storyHelpers';
import { STORY_UI } from './storyUiTokens';
import { SOLUTION_STORY_OPTIONS_QUERY_KEY } from './useSolutionStoryOptions';

export type IconMappings = {
  industry: Record<string, string>;
  industry_labels: Record<string, string>;
  practice: Record<string, string>;
  platform: Record<string, string>;
  techstack: Record<string, string>;
  company: Record<string, string>;
};

function normalizeKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_/]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function encodeAssetUrl(url: string): string {
  if (!url) return url;
  try {
    if (/^https?:\/\//i.test(url)) return url;
    const [pathPart, query = ''] = url.split('?');
    const encoded = pathPart
      .split('/')
      .map((seg) => (seg ? encodeURIComponent(seg) : seg))
      .join('/');
    return query ? `${encoded}?${query}` : encoded;
  } catch {
    return url;
  }
}

export function findMappedUrl(
  map: Record<string, string> | undefined,
  raw: string
): { key: string; url: string } | undefined {
  if (!map) return undefined;
  if (map[raw]) return { key: raw, url: map[raw] };

  const norm = normalizeKey(raw);
  for (const [key, url] of Object.entries(map)) {
    if (normalizeKey(key) === norm) return { key, url };
  }

  let best: { key: string; url: string; score: number } | undefined;
  for (const [key, url] of Object.entries(map)) {
    const kn = normalizeKey(key);
    if (!kn || !norm) continue;
    if (kn.includes(norm) || norm.includes(kn)) {
      const score = Math.min(kn.length, norm.length);
      if (!best || score > best.score) best = { key, url, score };
    }
  }
  return best ? { key: best.key, url: best.url } : undefined;
}

function resolveIndustryChip(
  industry: string | null | undefined,
  iconMappings: IconMappings | undefined
): { iconUrl?: string; label: string } | null {
  if (!industry?.trim()) return null;
  const raw = industry.trim();
  if (!iconMappings) return { label: raw };

  const byName = findMappedUrl(iconMappings.industry, raw);
  if (byName) {
    return {
      iconUrl: encodeAssetUrl(byName.url),
      label: iconMappings.industry_labels[byName.key] || byName.key,
    };
  }

  const norm = normalizeKey(raw);
  for (const [key, short] of Object.entries(iconMappings.industry_labels || {})) {
    if (normalizeKey(short) === norm || short.toUpperCase() === raw.toUpperCase()) {
      const url = iconMappings.industry[key];
      return {
        iconUrl: url ? encodeAssetUrl(url) : undefined,
        label: short,
      };
    }
  }

  return { label: raw };
}

function resolvePracticeChip(
  practice: string | null | undefined,
  iconMappings: IconMappings | undefined
): { iconUrl?: string; label: string } | null {
  if (!practice?.trim()) return null;
  const raw = practice.trim();
  if (!iconMappings?.practice) return { label: raw };

  const hit = findMappedUrl(iconMappings.practice, raw);
  if (hit) {
    return { iconUrl: encodeAssetUrl(hit.url), label: hit.key };
  }

  return { label: raw };
}

const ChipBox: React.FC<{
  iconUrl?: string;
  label?: string;
}> = ({ iconUrl, label }) => {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [iconUrl]);

  if (iconUrl && !imgFailed) {
    return (
      <Box
        title={label}
        sx={{
          height: { xs: 32, sm: 46 },
          minWidth: { xs: 32, sm: 46 },
          px: { xs: '5px', sm: '7px' },
          py: { xs: '5px', sm: '7px' },
          borderRadius: '4px',
          backgroundColor: '#041E42',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Box
          component="img"
          src={iconUrl}
          alt={label}
          sx={{
            height: { xs: 22, sm: 32 },
            width: 'auto',
            maxWidth: { xs: 84, sm: 120 },
            objectFit: 'contain',
            display: 'block',
          }}
          onError={() => setImgFailed(true)}
        />
      </Box>
    );
  }

  if (!label) return null;

  return (
    <Box
      sx={{
        height: { xs: 32, sm: 46 },
        minWidth: { xs: 32, sm: 46 },
        px: { xs: 0.75, sm: 1.25 },
        borderRadius: '4px',
        backgroundColor: '#041E42',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
      }}
      title={label}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '0.62rem', sm: '0.75rem' },
          letterSpacing: '0.02em',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const TagChips: React.FC<{
  story: SolutionStory;
  iconMappings: IconMappings | undefined;
}> = ({ story, iconMappings }) => {
  const industry = resolveIndustryChip(story.industry, iconMappings);
  const practice = resolvePracticeChip(story.practice, iconMappings);
  const regionLabel = story.region || null;

  let refLabel = story.referencable || null;
  if (refLabel) {
    const lower = refLabel.toLowerCase();
    if (lower.includes('non') || lower === 'no') {
      refLabel = 'NR';
    } else if (lower.includes('ref') || lower === 'yes') {
      refLabel = 'REF';
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      {industry && <ChipBox iconUrl={industry.iconUrl} label={industry.label} />}
      {regionLabel && <ChipBox label={regionLabel} />}
      {practice && <ChipBox iconUrl={practice.iconUrl} label={practice.label} />}
      {refLabel && <ChipBox label={refLabel} />}
    </Box>
  );
};

const paraSx = {
  fontSize: '12.5px',
  lineHeight: 1.65,
  color: STORY_UI.inkSoft,
  m: 0,
};

const colSx = {
  p: '26px 20px',
  borderLeft: `1px solid ${STORY_UI.line}`,
  minWidth: 0,
};

const colHeadingSx = {
  fontSize: '10.5px',
  fontWeight: 700,
  m: 0,
  mb: '14px',
  color: STORY_UI.inkFaint,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
};

function renderPointList(points: string[]) {
  if (!points.length) {
    return <Typography sx={paraSx}>—</Typography>;
  }
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: '16px',
        listStyleType: 'disc',
        color: STORY_UI.inkSoft,
      }}
    >
      {points.map((point, idx) => (
        <Box
          component="li"
          key={`${idx}-${point.slice(0, 24)}`}
          sx={{
            ...paraSx,
            pl: '2px',
            mb: idx < points.length - 1 ? '8px' : 0,
            display: 'list-item',
          }}
        >
          {point}
        </Box>
      ))}
    </Box>
  );
}

export interface SolutionStorySlidePreviewProps {
  story: SolutionStory;
  iconMappings?: IconMappings;
  showExecutiveSummary?: boolean;
  /** When false, icon mappings are not fetched (pass iconMappings instead). */
  loadIconMappings?: boolean;
  /** Which presentation tab's column layout to render. */
  activeTab?: StoryTab;
}

export const SolutionStorySlidePreview: React.FC<SolutionStorySlidePreviewProps> = ({
  story,
  iconMappings: iconMappingsProp,
  showExecutiveSummary = true,
  loadIconMappings = true,
  activeTab = 'completed',
}) => {
  const { data: optionsData } = useQuery({
    queryKey: SOLUTION_STORY_OPTIONS_QUERY_KEY,
    queryFn: () => solutionStoriesApi.getOptions(),
    staleTime: 5 * 60 * 1000,
    enabled: loadIconMappings && !iconMappingsProp,
  });

  const iconMappings = iconMappingsProp ?? (optionsData?.icon_mappings as IconMappings | undefined);

  const platforms = useMemo(() => splitCsv(story.platform), [story.platform]);
  const techStacks = useMemo(() => splitCsv(story.tech_stack), [story.tech_stack]);

  const displayTitle = story.solution_provided_title || '';
  const displayCompanyDetails =
    story.company_details || (story.is_deal_win_only ? story.winning_strategy_subheading || '' : '');
  const companyLogoUrl = findMappedUrl(iconMappings?.company, story.account_name || '')?.url;

  const businessContextPoints = collectPoints([
    story.business_context_1,
    story.business_context_2,
    story.business_context_3,
    story.business_context_4,
  ]);
  const solutionPoints = collectPoints([
    story.solution_1,
    story.solution_2,
    story.solution_3,
    story.solution_4,
  ]);
  const impactPoints = collectPoints([
    story.impact_1,
    story.impact_2,
    story.impact_3,
    story.impact_4,
  ]);

  const execSummary = `${story.account_name || 'The client'} engaged Searce to address critical business challenges through a modern, cloud-native approach. We proposed a comprehensive solution leveraging cutting-edge technology to drive measurable outcomes and long-term value.`;

  const clientColInner = (
    <>
      {companyLogoUrl ? (
        <Box sx={{ height: 50, maxWidth: '100%', mb: '16px', display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={companyLogoUrl}
            alt={story.account_name || ''}
            sx={{ height: '100%', width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
          />
        </Box>
      ) : (
        <Typography
          sx={{
            fontFamily: STORY_UI.fontDisplay,
            fontWeight: 700,
            fontSize: '16px',
            color: STORY_UI.accent,
            mb: '14px',
          }}
        >
          {clientShortName(story.account_name)}
        </Typography>
      )}
      <Typography sx={{ ...paraSx, color: STORY_UI.inkFaint, flex: 1 }}>
        {displayCompanyDetails || '—'}
      </Typography>
      {buildTagline(story) && (
        <Typography
          sx={{
            fontSize: '11px',
            color: STORY_UI.inkFaint,
            mt: 2,
            pt: '14px',
            borderTop: `1px solid ${STORY_UI.line}`,
          }}
        >
          {buildTagline(story)}
        </Typography>
      )}
    </>
  );

  const bizColInner = (
    <>
      <Typography component="h5" sx={colHeadingSx}>
        Business Context
      </Typography>
      {renderPointList(businessContextPoints)}
    </>
  );

  const solutionColInner = (
    <>
      <Typography component="h5" sx={colHeadingSx}>
        Solution
      </Typography>
      {renderPointList(solutionPoints)}
    </>
  );

  const impactColInner = (
    <>
      <Typography component="h5" sx={colHeadingSx}>
        Impact
      </Typography>
      {renderPointList(impactPoints)}
    </>
  );

  const techColInner = (
    <>
      <Typography component="h5" sx={{ ...colHeadingSx, color: STORY_UI.navy2 }}>
        Technology
      </Typography>

      {platforms.length > 0 && (
        <>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              color: STORY_UI.navy2,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              opacity: 0.7,
              mb: 1,
            }}
          >
            Platform
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
            {platforms.map((p) => {
              const hit = findMappedUrl(iconMappings?.platform, p);
              const iconUrl = hit?.url ? encodeAssetUrl(hit.url) : undefined;
              return (
                <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '9px',
                      backgroundColor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {iconUrl ? (
                      <Box
                        component="img"
                        src={iconUrl}
                        alt={p}
                        sx={{ width: '68%', height: '68%', objectFit: 'contain' }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <CloudIcon sx={{ fontSize: 20, color: STORY_UI.accent }} />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: STORY_UI.navy2 }}>
                    {p}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {techStacks.length > 0 && (
        <>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              color: STORY_UI.navy2,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              opacity: 0.7,
              mt: platforms.length ? '14px' : 0,
              pt: platforms.length ? '12px' : 0,
              borderTop: platforms.length ? '1px solid rgba(0,53,131,0.15)' : 'none',
              mb: 1,
            }}
          >
            Tech Stack
          </Typography>
          <Box
            sx={{
              display: 'grid',
              // Fixed tile size; the number of columns per row adapts to available width
              // instead of the tile stretching to fill a fixed column count.
              gridTemplateColumns: 'repeat(auto-fill, minmax(68px, 68px))',
              gap: 1,
            }}
          >
            {techStacks.map((t) => {
              const hit =
                findMappedUrl(iconMappings?.techstack, t) ||
                findMappedUrl(iconMappings?.platform, t) ||
                findMappedUrl(iconMappings?.practice, t);
              const iconUrl = hit?.url ? encodeAssetUrl(hit.url) : undefined;
              return (
                <Box key={t} sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: '9px',
                      backgroundColor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 0.75,
                    }}
                  >
                    {iconUrl ? (
                      <Box
                        component="img"
                        src={iconUrl}
                        alt={t}
                        sx={{ width: '65%', height: '65%', objectFit: 'contain' }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <CloudIcon sx={{ fontSize: 18, color: STORY_UI.accent }} />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '9.5px',
                      color: STORY_UI.navy2,
                      fontWeight: 500,
                      lineHeight: 1.3,
                      display: 'block',
                    }}
                  >
                    {t}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {platforms.length === 0 && techStacks.length === 0 && (
        <Typography sx={{ fontSize: '12px', color: STORY_UI.navy2, opacity: 0.7 }}>
          No technology info.
        </Typography>
      )}
    </>
  );

  const blankColInner = (heading: string) => (
    <>
      <Typography component="h5" sx={colHeadingSx}>
        {heading}
      </Typography>
      <Typography sx={paraSx}>—</Typography>
    </>
  );

  const textColInner = (heading: string, text: string | null | undefined) => (
    <>
      <Typography component="h5" sx={colHeadingSx}>
        {heading}
      </Typography>
      <Typography sx={paraSx}>{text?.trim() || '—'}</Typography>
    </>
  );

  const colCell = (
    key: string,
    inner: React.ReactNode,
    opts: { first?: boolean; tint?: boolean; flexCol?: boolean } = {}
  ) => (
    <Box
      key={key}
      sx={{
        ...colSx,
        ...(opts.first ? { borderLeft: 'none' } : {}),
        ...(opts.tint ? { backgroundColor: STORY_UI.accentTint } : {}),
        ...(opts.flexCol ? { display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      {inner}
    </Box>
  );

  // Rows instead of side-by-side columns: each block is full-width and its
  // height follows its own content, rather than every block being stretched to
  // match the tallest one (which is what a CSS grid row does to same-row items).
  const rowCell = (key: string, inner: React.ReactNode, opts: { first?: boolean } = {}) => (
    <Box
      key={key}
      sx={{
        p: '20px 20px',
        borderTop: opts.first ? 'none' : `1px solid ${STORY_UI.line}`,
      }}
    >
      {inner}
    </Box>
  );

  let columns: React.ReactNode[];
  let gridLg: string;
  const isRowLayout = activeTab === 'delivery';
  if (activeTab === 'won') {
    columns = [
      colCell('client', clientColInner, { first: true, flexCol: true }),
      colCell('biz', bizColInner),
      colCell('win', textColInner('Win Strategy', story.win_strategy)),
    ];
    gridLg = '1.2fr 1fr 1.2fr';
  } else if (activeTab === 'delivery') {
    const hasKeyObjectives = !!story.key_objectives_challenges;
    columns = [
      ...(hasKeyObjectives
        ? [rowCell('keyobjectives', textColInner('Key Objectives', story.key_objectives_challenges), { first: true })]
        : []),
      rowCell('challenges', textColInner('Challenges and Evolution', story.challenges_changed_since_inception), {
        first: !hasKeyObjectives,
      }),
      rowCell('techusage', textColInner('Technology Usage', story.tech_usage_way)),
      rowCell('platformusage', textColInner('Platform Usage', story.platform_usage_way)),
    ];
    gridLg = '';
  } else {
    columns = [
      colCell('client', clientColInner, { first: true, flexCol: true }),
      colCell('biz', bizColInner),
      colCell('solution', solutionColInner),
      colCell('impact', impactColInner),
      colCell('tech', techColInner, { tint: true }),
    ];
    gridLg = '1.1fr 1fr 1fr 1fr 1.1fr';
  }

  return (
    <>
      {showExecutiveSummary && (
        <Box sx={{ px: { xs: 2, sm: '34px' }, py: '22px', borderBottom: `1px solid ${STORY_UI.line}` }}>
          <Box
            sx={{
              borderRadius: STORY_UI.radiusMd,
              p: '1px',
              background: `linear-gradient(120deg, ${STORY_UI.accent}, ${STORY_UI.navy2}, ${STORY_UI.skyLight})`,
            }}
          >
            <Box
              sx={{
                backgroundColor: STORY_UI.surface,
                borderRadius: `calc(${STORY_UI.radiusMd} - 1px)`,
                px: '18px',
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: '11px',
                  fontWeight: 700,
                  color: STORY_UI.accentDeep,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                <SparkIcon sx={{ fontSize: 12 }} />
                Executive Summary
              </Box>
              <Typography sx={{ m: 0, fontSize: '13.5px', lineHeight: 1.6, color: STORY_UI.ink }}>
                {execSummary}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: STORY_UI.heroBg,
          px: { xs: 2, sm: '34px' },
          py: '26px',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'flex-start' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, md: 2.5 },
        }}
      >
        {/* Mobile-only top row: company name/logo + tag chips, so neither steals width from the title below */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          {story.account_name ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: STORY_UI.fontMono,
                  fontSize: '11px',
                  color: STORY_UI.accentDeep,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {story.account_name}
              </Typography>
            </Box>
          ) : (
            <Box />
          )}
          <TagChips story={story} iconMappings={iconMappings} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {story.account_name && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.75, mb: '6px' }}>
              <Typography
                sx={{
                  fontFamily: STORY_UI.fontMono,
                  fontSize: '11px',
                  color: STORY_UI.accentDeep,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {story.account_name}
              </Typography>
            </Box>
          )}
          <Typography
            sx={{
              fontFamily: STORY_UI.fontDisplay,
              fontWeight: 600,
              fontSize: { xs: '18px', sm: '22px' },
              letterSpacing: '-0.01em',
              color: STORY_UI.ink,
              m: 0,
              lineHeight: 1.3,
            }}
          >
            {displayTitle}
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TagChips story={story} iconMappings={iconMappings} />
        </Box>
      </Box>

      {isRowLayout ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', borderBottom: `1px solid ${STORY_UI.line}` }}>
          {columns}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
              lg: gridLg,
            },
            gap: 0,
            borderBottom: `1px solid ${STORY_UI.line}`,
          }}
        >
          {columns}
        </Box>
      )}
    </>
  );
};
